/**
 * Shared enums and scalars (§7.1).
 *
 * These are the single spelling of every enum that crosses the API boundary. Prisma has
 * its own generated enums and `@portal/domain` has its own string unions; this file is
 * what keeps all three saying the same words, and the `satisfies` assertions at the
 * bottom fail the build if they ever drift.
 */

import { z } from 'zod';
import type {
  ChecklistCategory,
  ItemScope,
  ItemState,
  Obligation,
  ResultOrigin,
  TeamRole,
} from '@portal/domain';

export const isoDateTime = z.string().datetime({ offset: true });

/** `A-05`. Mono everywhere in the UI, so it is worth validating as a shape. */
export const itemRef = z
  .string()
  .regex(/^[A-G]-\d{2}$/, 'An item reference looks like "A-05"');

export const cuid = z.string().min(1);

export const obligationSchema = z.enum(['MANDATORY', 'RECOMMENDED']);

export const itemScopeSchema = z.enum(['PROJECT', 'REPOSITORY', 'ENVIRONMENT']);

export const itemStateSchema = z.enum([
  'PENDING',
  'PARTIAL',
  'VERIFIED',
  'REJECTED',
  'NOT_APPLICABLE',
  'WAIVED',
]);

export const resultOriginSchema = z.enum(['FRESH', 'INHERITED']);

export const teamRoleSchema = z.enum(['PM', 'TL', 'DO', 'SEC', 'SUP', 'PO']);

/** Technology area of an item (issue #2). One per item; `null` on an uncategorised version. */
export const checklistCategorySchema = z.enum([
  'SOURCE_CONTROL',
  'INFRASTRUCTURE',
  'ACCESS_CONTROL',
  'DOCUMENTATION',
  'CI_CD',
  'DEPENDENCY_MANAGEMENT',
  'TESTING_QUALITY',
  'GOVERNANCE_REPORTING',
  'SECURITY',
  'MONITORING_OBSERVABILITY',
  'INCIDENT_SUPPORT',
  'BACKUP_DR',
]);

/** A project's category selection: every category, or an explicit non-empty list. */
export const categoryScopeSchema = z.union([
  z.literal('ALL'),
  z.array(checklistCategorySchema).min(1, 'Select at least one category'),
]);

/** §8.2 — portal-wide capability, mapped from an Entra group. */
export const orgRoleSchema = z.enum(['Admin', 'DeliveryManager', 'Auditor', 'Member', 'ReadOnly']);

export const auditStatusSchema = z.enum([
  'DRAFT',
  'IN_PROGRESS',
  'IN_REVIEW',
  'CLOSED',
  'CANCELLED',
  'ARCHIVED',
]);

export const maturityLevelSchema = z.enum(['L1', 'L2', 'L3', 'L4']);

export const freshnessStatusSchema = z.enum(['FRESH', 'EXPIRING_SOON', 'EXPIRED']);

export const severitySchema = z.enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']);

export const findingStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'CLOSED', 'WAIVED']);

export const waiverStatusSchema = z.enum(['REQUESTED', 'APPROVED', 'REJECTED', 'EXPIRED']);

export const probeStatusSchema = z.enum(['PASS', 'FAIL', 'INDETERMINATE']);

export const evidenceKindSchema = z.enum(['LINK', 'FILE_REF', 'DOCUMENT', 'CALENDAR_ENTRY']);

export const evidenceSystemSchema = z.enum([
  'GITLAB',
  'TEAMCITY',
  'DEPENDENCY_TRACK',
  'SONARQUBE',
  'AZURE',
  'ZAMMAD',
  'WIZ',
  'ZAP',
  'OTHER',
]);

export const hostingModelSchema = z.enum(['AZURE', 'HYBRID', 'ON_PREM']);

export const criticalitySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);

export const dataClassificationSchema = z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']);

export const exposureSchema = z.enum(['INTERNAL', 'PARTNER', 'PUBLIC']);

/** §5.2 — the six answers the registration wizard collects. */
export const contextProfileSchema = z.object({
  processesPersonalData: z.boolean(),
  publiclyExposed: z.boolean(),
  exposesPublicApi: z.boolean(),
  wizInScope: z.boolean(),
  hostingModel: hostingModelSchema,
  usesManagedServices: z.boolean(),
});

export type Criticality = z.infer<typeof criticalitySchema>;
export type DataClassification = z.infer<typeof dataClassificationSchema>;
export type Exposure = z.infer<typeof exposureSchema>;
export type HostingModel = z.infer<typeof hostingModelSchema>;
export type EvidenceKind = z.infer<typeof evidenceKindSchema>;
export type EvidenceSystem = z.infer<typeof evidenceSystemSchema>;
export type FindingStatus = z.infer<typeof findingStatusSchema>;
export type ProbeStatus = z.infer<typeof probeStatusSchema>;
export type FreshnessStatus_ = z.infer<typeof freshnessStatusSchema>;
export type MaturityLevel_ = z.infer<typeof maturityLevelSchema>;

export type Obligation_ = z.infer<typeof obligationSchema>;
export type ItemScope_ = z.infer<typeof itemScopeSchema>;
export type ItemState_ = z.infer<typeof itemStateSchema>;
export type ResultOrigin_ = z.infer<typeof resultOriginSchema>;
export type TeamRole_ = z.infer<typeof teamRoleSchema>;
export type ChecklistCategory_ = z.infer<typeof checklistCategorySchema>;
export type CategoryScopeDto = z.infer<typeof categoryScopeSchema>;
export type OrgRole = z.infer<typeof orgRoleSchema>;
export type AuditStatus = z.infer<typeof auditStatusSchema>;
export type Severity = z.infer<typeof severitySchema>;
export type ContextProfileDto = z.infer<typeof contextProfileSchema>;

// If the domain package renames a state, these lines fail to compile rather than
// letting the API and the engine disagree at runtime.
type Assert<A, B extends A> = B;
export type _ObligationMatches = Assert<Obligation, Obligation_>;
export type _ScopeMatches = Assert<ItemScope, ItemScope_>;
export type _StateMatches = Assert<ItemState, ItemState_>;
export type _OriginMatches = Assert<ResultOrigin, ResultOrigin_>;
export type _RoleMatches = Assert<TeamRole, TeamRole_>;
export type _CategoryMatches = Assert<ChecklistCategory, ChecklistCategory_>;
export type _CategoryMatchesBack = Assert<ChecklistCategory_, ChecklistCategory>;

/**
 * The twelve categories with the names people use for them — for the category picker on
 * the project overview. Here for the same reason as `TEAM_ROLES`: two copies drift.
 */
export const CHECKLIST_CATEGORY_NAMES: ReadonlyArray<{
  readonly category: ChecklistCategory_;
  readonly name: string;
}> = [
  { category: 'SOURCE_CONTROL', name: 'Source control' },
  { category: 'INFRASTRUCTURE', name: 'Infrastructure & environments' },
  { category: 'ACCESS_CONTROL', name: 'Access control' },
  { category: 'DOCUMENTATION', name: 'Documentation' },
  { category: 'CI_CD', name: 'CI/CD' },
  { category: 'DEPENDENCY_MANAGEMENT', name: 'Dependency management' },
  { category: 'TESTING_QUALITY', name: 'Testing & quality' },
  { category: 'GOVERNANCE_REPORTING', name: 'Governance & reporting' },
  { category: 'SECURITY', name: 'Security' },
  { category: 'MONITORING_OBSERVABILITY', name: 'Monitoring & observability' },
  { category: 'INCIDENT_SUPPORT', name: 'Incident response & support' },
  { category: 'BACKUP_DR', name: 'Backup & disaster recovery' },
];

export function checklistCategoryName(category: ChecklistCategory_): string {
  return CHECKLIST_CATEGORY_NAMES.find((entry) => entry.category === category)?.name ?? category;
}

/**
 * The six team roles with the names people actually use for them.
 *
 * Here rather than in a screen because more than one screen needs it — registration and
 * the project overview's ownership register — and two copies of a list like this drift.
 *
 * Appendix B.1 lists TEN roles; this enum holds six. Incident Commander, Communication
 * Owner, Postmortem Owner and Data Protection Contact cannot be recorded at all, which
 * is an enum change and a migration rather than a screen problem (docs/DEFERRED.md).
 */
export const TEAM_ROLES: ReadonlyArray<{ readonly role: TeamRole_; readonly name: string }> = [
  { role: 'PM', name: 'Project Manager' },
  { role: 'TL', name: 'Technical Lead' },
  { role: 'DO', name: 'DevOps / Platform' },
  { role: 'SEC', name: 'Security Lead' },
  { role: 'SUP', name: 'Support Lead' },
  { role: 'PO', name: 'Product Owner' },
];

export function teamRoleName(role: TeamRole_): string {
  return TEAM_ROLES.find((entry) => entry.role === role)?.name ?? role;
}
